import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import i18n from "@/i18n";

// Extract error message from API response
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    // Try to get message from backend response
    const backendMessage = error.response?.data?.message;
    if (backendMessage && typeof backendMessage === "string") {
      return backendMessage;
    }

    // Handle server errors (500+) without response message
    const status = error.response?.status;
    if (status && status >= 500) {
      return i18n.t("common.errors.api_default");
    }

    // Handle network errors (no response)
    if (!error.response) {
      return i18n.t("common.errors.api_default");
    }

    // Fallback to axios error message for other cases
    if (error.message) {
      return error.message;
    }
  }
  // Generic error
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return i18n.t("common.errors.api_default");
};

// Custom retry function - only retry for network errors and server errors (500+)
export const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 2) return false;

  if (error instanceof AxiosError) {
    // Don't retry if we got a response with status < 500 (client errors like 400, 401, 404)
    if (error.response && error.response.status < 500) {
      return false;
    }
    // Retry for network errors (no response) or server errors (500+)
    return true;
  }
  return false;
};

// Initialize QueryClient with global error handling for both queries and mutations
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Skip if query has custom error handling (meta.skipGlobalError = true)
      if (query.options.meta?.skipGlobalError) {
        return;
      }
      // Show global error toast
      const message = getErrorMessage(error);
      toast.error(message);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if mutation has custom error handling (meta.skipGlobalError = true)
      if (mutation.options.meta?.skipGlobalError) {
        return;
      }
      // Show global error toast
      const message = getErrorMessage(error);
      toast.error(message);
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
    mutations: {
      retry: shouldRetry,
    },
  },
});
